import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

declare global {
    interface Window {
        bgAudio: HTMLAudioElement;
    }
}

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        <!-- max-width: 1024px; -->
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

.title {
  text-align: center;
  justify-content: center;
  overflow: auto;
  justify-content: end;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
  background-color: rgba(0, 0, 0, 0.7);
}

    .header {
        display: flex;
        align-items: center;
        position: relative;
        justify-content: center;
        top: 125px;
        margin-top: 10px;
    }

.hp-bar {
    position: relative;
    width: 75%;
    height: 10px;
    margin-top: 10px;
    border-radius: 10px;
}

.hp-bar__default {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: white;
    border-radius: 10px;
    z-index: 1;
}

.hp-bar__inner { 
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: red;
    border-radius: 10px;
    transition: width 0.5s;
    z-index: 2;
}

    .header img {
        width: auto;
        height: 600px;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        display: flex; 
        justify-content: center;
        overflow: auto;
        margin-top: 290px;
        text-align: center;
        padding: 10px 10px;
        background-color: rgba(0, 0, 0, 0.7);
    }

    .content p {
        width: 1400px;
        margin: 0 0 10px 0;
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #52478b;
        border: 1px solid #332c57;
        display: flex;
        height: 105px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: #7f6ed7;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
    }
`;
interface PeterQuestion {
    question: string;
    answer: string;
}
const audioContext: AudioContext = new AudioContext(); // VOORKOM DAT ALLE AUDIO DOOR ELKAAR HEEN LOOPT
let currentSource: AudioBufferSourceNode;
let unmuteTimeout: number | null = null;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private interval: NodeJS.Timeout | null = null;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const roomImages: string[] | undefined = this._currentGameState?.roomImages;
        const backgroundImage: string = roomImages && roomImages.length > 0
            ? `/assets/img/rooms/${roomImages[0]}.png`
            : "/assets/img/rooms/default.png";

        const dynamicStyle: string = `
            :host {
                background-image: url('${backgroundImage}');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
            }
        `;
        const elements: HTMLElement[] = htmlArray`
        <style>
        ${styles}
        ${dynamicStyle}
        .overlay-inventory {
            position: absolute;
            top: 180px;
            left: 10px;
            z-index: 10;
            background-color: rgba(0, 0, 0, 0.6);
            pointer-events: none;
            margin: 0;
            padding: 10px 10px 10px 10px;
        }

        .overlay-inventory h1 {
            font-size: 30px;
            margin-top: 0;
            margin-bottom: 8px;
            padding: 0;
        }

        .overlay-inventory p {
            font-size: 20px;
            margin-top: 0;
            margin-bottom: 4px;
            padding: 0;
            line-height: 1.1;
        }
        .overlay-inventory p:last-child {
             margin-bottom: 0;
        }

        </style>

        ${this.renderTitle()}
        ${this.renderHeader()}
        <div class="overlay-inventory">
            <h1> Inventory </h1>
            <p>Beer: ${this._currentGameState?.data.hasGivenBeer ? 1 : 0} </p>
            <p>EHBO Kit: ${this._currentGameState?.data.ehboKit} </p> 
            <p>Flashlight: ${this._currentGameState?.data.hasFlashlight ? 1 : 0} </p>
            <p>Parachute: ${this._currentGameState?.data.hasParachute ? 1 : 0} </p>
            <p>Money: ${this._currentGameState?.data.hasMoney ? 1 : 0} </p>
            <p>Oxygen Tank: ${this._currentGameState?.data.hasOxygenTank ? 1 : 0} </p>
            <p>Secret code: ${this._currentGameState?.data.hasSecretCode ? localStorage.getItem("secretCode") : "No"} </p>
        </div>
        ${this.renderContent()}
        ${this.renderFooter()}
    `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): HTMLElement {
        const hp: number | undefined = this._currentGameState?.data.hp;
        const roomAlias: string | undefined = this._currentGameState?.roomAlias; // Huidige kamer

        // Stop de timer als de speler in de "Crashing airplane Room" komt, maar reset NIET
        if (roomAlias === "Victory" || roomAlias === "Death") {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        }
        else if (!this.interval && roomAlias !== "Start") {
            this.interval = setInterval(() => this.startTimer(), 10);
        }

        if (roomAlias === "Start") {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
            localStorage.setItem("time", JSON.stringify({ tens: 0, seconds: 0, minutes: 0 }));
        }
        // Retrieve and parse the stored time from localStorage
        const storedTimeStr: string | null = localStorage.getItem("time");
        const time: { tens: number; seconds: number; minutes: number } = storedTimeStr
            ? JSON.parse(storedTimeStr) as { tens: number; seconds: number; minutes: number }
            : { tens: 0, seconds: 0, minutes: 0 };

        // Format the time values with leading zeros if needed
        const formattedTens: string = time.tens < 10 ? `0${time.tens}` : time.tens.toString();
        const formattedSeconds: string = time.seconds < 10 ? `0${time.seconds}` : time.seconds.toString();
        const formattedMinutes: string = time.minutes < 10 ? `0${time.minutes}` : time.minutes.toString();

        return html`
            <div class="title">
                <div> <!-- Room and Timer Info -->
                    Room: ${roomAlias ?? "N/A"} | Timer: <span id="minutes">${formattedMinutes}</span>:<span id="seconds">${formattedSeconds}</span>:<span id="tens">${formattedTens}</span>
                </div>
                
                    <div class="hp-bar">
                        <div class="hp-bar__default"></div>
                        <div class="hp-bar__inner" style="width: ${hp}%;"></div>
                    </div>
                    <span class="hp-text" style="margin-top: 5px;">${hp} / 100</span> <!-- HP Text -->
                </div>
            </div>
        `;
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.slice(1).map(url => `<img src="/assets/img/rooms/characters/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
            <div class="buttons">
                <div>
                ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                </div>
                <div>
                ${this._selectedActionButton
                      ? this._currentGameState?.objects
                          .filter(button => {
                              if (this._selectedActionButton!.name === "Use" && button.name !== "EHBO") {
                                  return false;
                              }
                              if (this._selectedActionButton!.name === "Talk To" && button.name !== "Peter Griffin" && button.name !== "Philip Shimano" && button.name !== "Captain Jack" && button.name !== "Barman") {
                                  return false;
                              }
                              return true;
                          })
                          .map(button => this.renderGameObjectButton(button)) ||
                          ""
                    : ""
                }
                </div>
            </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            

            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        this.handlePeterAudio(button);
        this.handleLafeberAudio(button);
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    private handleSecretCode(button: GameObjectReference): void {
        if (button.alias === "SecretCode") {
            localStorage.setItem("secretCode", `${Math.floor(Math.random() * 10000)}`.padStart(4)
            );
        }
    }

    private startTimer(): void {
        if (!this.shadowRoot) return;
        const storedTime: string | null = localStorage.getItem("time");
        let { tens, seconds, minutes }: { tens: number; seconds: number; minutes: number } = storedTime
            ? (JSON.parse(storedTime) as { tens: number; seconds: number; minutes: number })
            : { tens: 0, seconds: 0, minutes: 0 };

        tens = parseInt(String(tens)) + 1;

        if (tens >= 100) {
            seconds = parseInt(String(seconds)) + 1;
            tens = 0;
        }

        if (seconds >= 60) {
            minutes = parseInt(String(minutes)) + 1;
            seconds = 0;
        }

        localStorage.setItem("time", JSON.stringify({ tens, seconds, minutes }));

        const appendTens: HTMLElement | null = this.shadowRoot.getElementById("tens") as HTMLElement;
        const appendSeconds: HTMLElement | null = this.shadowRoot.getElementById("seconds") as HTMLElement;
        const appendMinutes: HTMLElement | null = this.shadowRoot.getElementById("minutes") as HTMLElement;

        appendTens.innerText = tens < 10 ? `0${tens}` : tens.toString();
        appendSeconds.innerText = seconds < 10 ? `0${seconds}` : seconds.toString();
        appendMinutes.innerText = minutes < 10 ? `0${minutes}` : minutes.toString();
    }

    private peterQuestions: PeterQuestion[] = [
        {
            question: "In TikTok slang, what does having riz generally mean?",
            answer: "Having impressive romantic charisma",
        },
        {
            question: "Which viral meme phrase often points to bizarre or chaotic scenarios, humorously blaming the state they happen in?",
            answer: "Only in Ohio",
        },
        {
            question: "If you get a FaceTime call from John Pork, what should you do? ",
            answer: "Cry, because you know it's over",
        },
        {
            question: "Which popular sound on 'Ohio TikTok' jokes about an 'Ohio Final Boss' and surreal scenarios?",
            answer: "Down in Ohio by Lil B",
        },
        {
            question: "what is the name of the most viral party?",
            answer: "Tiktok rizz party",
        },
    ];

    private handlePeterAudio(button: GameObjectReference): void {
        const onlyQuizItems: number = parseInt(button.alias.split(":")[2]);
        if (this._currentGameState?.data.hasDefeatedPeterGriffin) return;
        if (button.name === "Yes" || button.name === "Try again") {
            this.playAudio("/assets/audio/peter/0.mp3");
            return;
        }
        if (button.name === "No") {
            this.playAudio("/assets/audio/peter/No.mp3");
            return;
        }
        if (!button.alias.startsWith("talk:Peter Griffin") || onlyQuizItems > 4) {
            return;
        }
        const foundIndex: number = this.peterQuestions.findIndex(q =>
            button.name.includes(q.answer)
        );

        if (foundIndex === -1) {
            this.playAudio("/assets/audio/peter/laughing.mp3");
            return;
        }
        else if (foundIndex < 4) {
            this.playAudio(`/assets/audio/peter/${foundIndex + 1}.mp3`);
            return;
        }
        else {
            this.playAudio("/assets/audio/peter/completed.mp3");
        }
    }

    private lafeberQuestions: PeterQuestion[] = [
        {
            question: "スクラムマスターの主な責任は何ですか? (What is the main responsibility of a Scrum Master?)",
            answer: "Removing obstacles and facilitating the Scrum process",
        },
        {
            question: "スプリントレビューとは何ですか? その目的は何ですか? (What is a Sprint Review and what is its purpose?)",
            answer: "A meeting in which the development team asks stakeholders for feedback on the work delivered",
        },
        {
            question: "どの成果物がスクラムに属しますか? (Which artifacts belong to Scrum?)",
            answer: "Product Backlog, Sprint Backlog, Increment",
        },
        {
            question: "バーンダウンチャートとは何ですか? (What is a burndown chart?)",
            answer: "A graph visualizing the remaining workload in a sprint",
        },
        {
            question: "Scrum における典型的なスプリントの長さはどれくらいですか? (How long is a typical sprint in Scrum?)",
            answer: "2 to 4 weeks",
        },
    ];

    private handleLafeberAudio(button: GameObjectReference): void {
        const onlyQuizItems: number = parseInt(button.alias.split(":")[2]);

        if (button.name === "es") {
            this.playAudio("/assets/audio/soundtrack/yakuza0intro.mp3");
        }
        if (button.name === "yes" || button.name === "try again") {
            this.playAudio("/assets/audio/lafeber/1lafeberscrumrolshort.mp3");
            return;
        }
        else if (button.name === "Removing obstacles and facilitating the Scrum process") {
            this.playAudio("/assets/audio/lafeber/2sprintreviewshorter.mp3");
            return;
        }
        else if (button.name === "A meeting in which the development team asks stakeholders for feedback on the work delivered") {
            this.playAudio("/assets/audio/lafeber/3artifactsshorter.mp3");
            return;
        }
        else if (button.name === "Product Backlog, Sprint Backlog, Increment") {
            this.playAudio("/assets/audio/lafeber/4.q.burndown-chart.mp3");
            return;
        }
        else if (button.name === "A graph visualizing the remaining workload in a sprint") {
            this.playAudio("/assets/audio/lafeber/5sprintlengthshorter.mp3");
            return;
        }

        if (!button.alias.startsWith("talk:Philip Shimano") || onlyQuizItems > 4) {
            return;
        }
        const foundIndex: number = this.lafeberQuestions.findIndex(q =>
            button.name.includes(q.answer)
        );

        if (foundIndex === -1) {
            this.playAudio("/assets/audio/lafeber/2.q.sprint-review-why.mp3");
            return;
        }
        else if (foundIndex < 4) {
            this.playAudio(`/assets/audio/lafeber/${foundIndex + 1}.q.${button.alias.split(":")[2]}.mp3`);
            return;
        }
        else {
            this.playAudio("/assets/audio/lafeber/5.q.sprint-length.mp3");
        }
    }

    private playAudio(audioUrl: string): void {
        window.bgAudio.muted = true;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (currentSource) currentSource.stop();
        if (unmuteTimeout) {
            clearTimeout(unmuteTimeout);
            unmuteTimeout = null;
        }
        const audioSource: AudioBufferSourceNode = audioContext.createBufferSource();

        fetch(audioUrl)
            .then(response => response.arrayBuffer())
            .then(arrayBuffer => audioContext.decodeAudioData(arrayBuffer))
            .then(decodedData => {
                audioSource.buffer = decodedData;
                audioSource.connect(audioContext.destination);
                audioSource.start();
                currentSource = audioSource;
                unmuteTimeout = window.setTimeout(() => {
                    window.bgAudio.muted = false;
                }, (decodedData.duration + 1) * 1000);
            }).catch((error: unknown) => {
                console.error("Error loading or playing audio:", error);
            });
    }
    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */

    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        this.handleSecretCode(button);
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }
        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        if (button.name === "Peter Griffin" && this._currentGameState?.data.hasBeer && !this._currentGameState.data.hasDefeatedPeterGriffin) {
            this.playAudio("/assets/audio/peter/intro.mp3");
        };
        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
